import { useCategoricalBarChartBaseConfig } from './useCategoricalBarChartBaseConfig.js';
import { type ColorToken } from '../../core/types/color-palette.js';
import { getColorPalette } from '../../core/utils/get-color-palette.js';
import type { CategoricalChartData } from '../contexts/CategoricalBarChartDataContext.js';
import type {
  CategoricalBarData,
  CategoryMetadata,
  Dimension,
  DimensionMetadata,
  WeakMapCategory,
  WeakMapDimension,
} from '../types/categorical-bar-chart-data.js';
import type { CategoricalBarChartData } from '../types/categorical-bar-chart.js';
import { isGrouped } from '../utils/layout.js';

function createCategoryMetadata(id: string, name: string): CategoryMetadata {
  return { id, name };
}

/**
 * Generate bar data and weakmaps to be filled into categorical bar chart provider
 * - generate single or multiple dimensions object from consumer with ids
 * - generate colors by dimensions

 *
 * @param initialData - Categorical data from component
 * @returns Categorical chart data
 */
export function useGenerateCategoricalBarData(
  initialData: CategoricalBarChartData[],
): CategoricalChartData {
  const { valueRepresentation, colorPalette, colorPaletteMode, groupMode } =
    useCategoricalBarChartBaseConfig();

  const isRelative = valueRepresentation === 'relative';
  const firstCategoryUnit = initialData[0]?.unit;

  const uniqueDimensions = new Set<string>();
  const categoricalData: CategoricalBarData[] = [];
  const categoryMap: WeakMapCategory = new WeakMap();
  const dimensionMap: WeakMapDimension = new WeakMap();
  const colorByDimension: Map<string, ColorToken> = new Map();

  for (const categoryData of initialData) {
    const categoryId = crypto.randomUUID();
    const {
      unit,
      category: categoryName,
      value: categoryValue,
      color: categoryColor,
      ...rest
    } = categoryData;
    const newCategory: CategoricalBarData = {
      id: categoryId,
      name: categoryName,
      unit,
      dimensions: [],
      ...rest,
    };

    const isSingleDimension =
      unit !== firstCategoryUnit || typeof categoryValue !== 'object';

    const categoricalColorPalette =
      colorPaletteMode === 'multi-color' ||
      isGrouped(groupMode) ||
      !isSingleDimension;

    if (isSingleDimension) {
      const value = categoryValue as number;
      const colorPositionIndex = initialData.indexOf(categoryData);
      const paletteColorPosition = categoricalColorPalette
        ? colorPositionIndex
        : 0;
      const dimensionName = categoryName;
      const id = crypto.randomUUID();

      let color: ColorToken;
      if (colorByDimension.has(dimensionName)) {
        // Need to cast here, as a map will not give you type safety if it is exists
        color = colorByDimension.get(dimensionName) as ColorToken;
      } else if (categoryColor) {
        color = { Default: categoryColor };
      } else {
        color = getColorPalette(
          categoryName,
          paletteColorPosition,
          colorPalette,
          colorPaletteMode,
        );
      }
      colorByDimension.set(dimensionName, color);

      const newDimension: Dimension = {
        ...rest,
        id,
        name: dimensionName,
        value,
      };

      const dimensionMetadata: DimensionMetadata = {
        id,
        name: dimensionName,
        value,
        color,
      };

      uniqueDimensions.add(dimensionName);
      dimensionMap.set(newDimension, dimensionMetadata);
      newCategory.dimensions.push(newDimension);
    } else {
      Object.entries(categoryValue).forEach(([dimensionKey, value], index) => {
        const paletteColorPosition = categoricalColorPalette ? index : 0;

        let color: ColorToken;
        if (colorByDimension.has(dimensionKey)) {
          color = colorByDimension.get(dimensionKey) as ColorToken;
        } else {
          color = getColorPalette(
            dimensionKey,
            paletteColorPosition,
            colorPalette,
          );
        }

        colorByDimension.set(dimensionKey, color);

        const id = crypto.randomUUID();
        const name = dimensionKey;

        const newDimension: Dimension = {
          ...rest,
          id,
          name,
          value,
          category: categoryName,
        };

        const dimensionMetadata: DimensionMetadata = {
          id,
          name,
          value,
          color,
        };

        uniqueDimensions.add(dimensionKey);
        dimensionMap.set(newDimension, dimensionMetadata);
        newCategory.dimensions.push(newDimension);
      });
    }

    const categoryMedatada = createCategoryMetadata(categoryId, categoryName);
    categoryMap.set(newCategory, categoryMedatada);
    categoricalData.push(newCategory);
  }

  return {
    categoryMap,
    dimensionMap,
    categoricalData,
    uniqueDimensions: Array.from(uniqueDimensions),
    unit: isRelative ? 'percent' : firstCategoryUnit,
    valueRepresentation,
  };
}
